import { useContext } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  Text,
  FilterAltIcon,
  CaretDownIcon,
  PopoverMenu,
  PopoverMenuItem,
  usePopoverMenu,
  NakedButton,
  CheckboxField,
  useFormField,
  RadioField,
} from '@frontend/design-system';
import { IN_NETWORK, OUT_OF_NETWORK } from '../constants';
import { InsuranceReportContext } from '../dental-insurance-eligibility-report';

export const Filters = () => {
  const { filters, coverageTypes, showNetworkFilter, setFilters } = useContext(InsuranceReportContext);

  const { t } = useTranslation('insuranceEligibilityReport');
  const { getMenuProps, getTriggerProps } = usePopoverMenu({ placement: 'bottom' });
  const popoverMenuTriggerProps = getTriggerProps();
  const menuProps = getMenuProps();
  const checkboxFieldProps = useFormField({ type: 'checkbox' });
  const radioFieldProps = useFormField({
    type: 'radio',
    value: filters.inNetworkSelected ? IN_NETWORK : OUT_OF_NETWORK,
  });

  if (showNetworkFilter || coverageTypes?.length > 1) {
    return (
      <>
        <NakedButton css={[buttonStyle, filterViewButtonStyle]} {...popoverMenuTriggerProps}>
          <FilterAltIcon size={16} />
          <Text css={filterViewTextStyle}>{t('Filter View')}</Text>
          <CaretDownIcon size={16} />
        </NakedButton>

        <PopoverMenu {...menuProps} css={{ minWidth: theme.spacing(29) }}>
          {showNetworkFilter && (
            <>
              <Text css={titleStyle}>{t('Network')}</Text>
              <RadioField labelPlacement='left' name='networkSelectorRadio' {...radioFieldProps}>
                <PopoverMenuItem
                  css={popoverButtonStyle}
                  onClick={() => setFilters({ ...filters, inNetworkSelected: true })}
                >
                  <RadioField.Radio value={IN_NETWORK}>{t('In Network')}</RadioField.Radio>
                </PopoverMenuItem>
                <PopoverMenuItem
                  css={popoverButtonStyle}
                  onClick={() => setFilters({ ...filters, inNetworkSelected: false })}
                >
                  <RadioField.Radio value={OUT_OF_NETWORK}>{t('Out Of Network')}</RadioField.Radio>
                </PopoverMenuItem>
              </RadioField>
            </>
          )}
          {coverageTypes?.length > 1 && (
            <>
              <Text css={titleStyle}>
                {t('Type')}
                <NakedButton onClick={() => setFilters({ ...filters, coverage: coverageTypes })} css={showAllStyle}>
                  {t('Show All')}
                </NakedButton>
              </Text>
              {coverageTypes.map((coverage: string, ix: number) => {
                const selected = filters?.coverage?.includes(coverage);
                const onChange = () => {
                  setFilters({
                    ...filters,
                    coverage: selected
                      ? filters.coverage.filter((c) => c !== coverage)
                      : [...filters.coverage, coverage],
                  });
                };

                return (
                  <PopoverMenuItem css={popoverButtonStyle} key={`header-popover-item-${coverage}-${ix}`}>
                    <CheckboxField
                      name={coverage}
                      {...checkboxFieldProps}
                      css={checkboxStyle}
                      label={coverage}
                      labelPlacement='left'
                      value={selected}
                      onChange={onChange}
                    />
                  </PopoverMenuItem>
                );
              })}
            </>
          )}
        </PopoverMenu>
      </>
    );
  }
  return null;
};

const buttonStyle = css`
  width: auto;
  white-space: nowrap;
  padding: ${theme.spacing(1, 2)};
`;

const filterViewButtonStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${theme.colors.white};
  padding: ${theme.spacing(0, 1)};
  border-radius: ${theme.borderRadius.small};
  border: 1px solid ${theme.colors.neutral20};
`;

const filterViewTextStyle = css`
  padding: ${theme.spacing(0, 1)};
`;

const popoverButtonStyle = css`
  display: flex;
  justify-content: space-between;
  text-align: left;
  width: 100%;
  align-items: center;
  svg {
    fill: ${theme.colors.white};
  }
`;

const titleStyle = css`
  padding: ${theme.spacing(0, 2)};
  color: ${theme.colors.neutral50};
  border-bottom: ${theme.spacing(0.125)} solid;
  border-color: ${theme.colors.neutral10};
  display: flex;
  justify-content: space-between;
`;

const showAllStyle = css`
  padding: ${theme.spacing(0, 0, 0, 2)};
  color: ${theme.colors.primary50};
`;

const checkboxStyle = css`
  width: 100%;
  justify-content: flex-start;
`;
